import React from 'react';
import { ShieldCheck, CheckCircle, Clock, MapPin, Briefcase, FileCheck } from 'lucide-react';
import { StorytellingLayout, ExpandableCard } from '../base';

/**
 * Tela de storytelling para Visto Americano
 * 
 * Copy focada no diferencial real da DSC em cada cenário:
 * - Primeira via: "a parte difícil é nossa, você só comparece"
 * - Renovação sem entrevista: "resolve sem sair de casa"
 * - Renovação com entrevista: "preparação completa pra você ir confiante"
 * 
 * Schema esperado (details):
 * - service, visa_type, interview_required
 * - includes: string[]
 * - consulate_cities: string[] (com entrevista)
 * - renewal_note: string (sem entrevista)
 */

const NARRATIVES = {
  no_interview: 'Renovação sem burocracia e sem deslocamento. A DSC Travel coleta seu passaporte, cuida de toda a documentação e devolve em suas mãos com o visto renovado. Você não precisa ir a lugar nenhum.',
  with_interview: 'A DSC Travel prepara toda a sua documentação, preenche o DS-160 e te orienta passo a passo para a entrevista consular. Você entra no consulado sabendo exatamente o que esperar.',
  new: 'A burocracia do visto americano é por nossa conta. A DSC Travel cuida do formulário DS-160, da documentação e da preparação para entrevista — você só precisa comparecer ao consulado.'
};

export default function StorytellingVisa({
  block,
  stepIndicator,
  narrativeText,
  ctaText,
  onContinue,
  onBack,
  onShare
}) {
  const details = block.details || {};
  const includes = details.includes || [];
  const interviewRequired = details.interview_required;
  const consulateCities = details.consulate_cities || [];

  const backgroundImage = 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=800&q=80';

  const formatPrice = (value) => {
    if (!value && value !== 0) return '';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Narrativa default baseada no contexto
  const getDefaultNarrative = () => {
    if (details.visa_type === 'new') return NARRATIVES.new;
    return interviewRequired ? NARRATIVES.with_interview : NARRATIVES.no_interview;
  };

  // Subtítulo contextual
  const getSubtitle = () => {
    if (details.visa_type === 'new') return 'Primeira via — com entrevista consular';
    if (!interviewRequired) return 'Renovação — sem entrevista';
    return 'Renovação — com entrevista consular';
  };

  // Highlight contextual (a diferença chave entre os fluxos)
  const getHighlight = () => {
    if (!interviewRequired) {
      return {
        icon: <Briefcase size={20} color="#5DD4C3" strokeWidth={2} />,
        title: 'Sem deslocamento ao consulado',
        description: 'DSC coleta e devolve seu passaporte em mãos'
      };
    }
    return {
      icon: <MapPin size={20} color="#5DD4C3" strokeWidth={2} />,
      title: 'Preparação completa para entrevista',
      description: consulateCities.length > 0
        ? `Consulados: ${consulateCities.join(' · ')}`
        : 'Consulados: SP · RJ · BSB · Recife · Porto Alegre'
    };
  };

  const highlight = getHighlight();

  const cardContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Ícone + Título */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{
          width: '48px',
          height: '48px',
          backgroundColor: 'rgba(93, 212, 195, 0.15)',
          borderRadius: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <ShieldCheck size={24} color="#5DD4C3" strokeWidth={2} />
        </div>
        <div>
          <p style={{
            fontSize: '1.0625rem',
            fontWeight: 600,
            color: 'white',
            margin: 0,
            lineHeight: 1.3
          }}>
            {block.title || 'Visto Americano'}
          </p>
          <p style={{
            fontSize: '0.8125rem',
            color: 'rgba(255, 255, 255, 0.6)',
            margin: '0.25rem 0 0 0'
          }}>
            {getSubtitle()}
          </p>
        </div>
      </div>

      {/* Destaque: preço tudo incluso */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.875rem',
        backgroundColor: 'rgba(93, 212, 195, 0.1)',
        borderRadius: '0.75rem',
        borderLeft: '3px solid #5DD4C3'
      }}>
        <div style={{
          width: '44px', height: '44px',
          backgroundColor: 'rgba(93, 212, 195, 0.15)',
          borderRadius: '0.5rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0
        }}>
          {highlight.icon}
        </div>
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#5DD4C3', margin: 0 }}>
            {highlight.title}
          </p>
          <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', margin: '0.125rem 0 0 0' }}>
            {highlight.description}
          </p>
        </div>
      </div>

      {/* Grid: Valor / DS-160 / Acompanhamento */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Valor tudo incluso */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{
            width: '44px', height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <ShieldCheck size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {block.price ? formatPrice(block.price) : 'R$ 1.850'}
          </p>
          <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Tudo incluso
          </p>
        </div>

        {/* DS-160 incluso */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{
            width: '44px', height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <FileCheck size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            DS-160
          </p>
          <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Formulário incluso
          </p>
        </div>

        {/* Acompanhamento */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{
            width: '44px', height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Clock size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            Integral
          </p>
          <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Acompanhamento
          </p>
        </div>
      </div>

      {/* Preview inclusos */}
      {includes.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          paddingTop: '0.75rem',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          {includes.slice(0, 3).map((item, idx) => (
            <div key={idx} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              backgroundColor: 'rgba(93, 212, 195, 0.15)',
              borderRadius: '0.5rem'
            }}>
              <CheckCircle size={14} color="#5DD4C3" strokeWidth={2} />
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#5DD4C3' }}>
                {item}
              </span>
            </div>
          ))}
          {includes.length > 3 && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.5rem 0.75rem',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '0.5rem'
            }}>
              <span style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                +{includes.length - 3} itens
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const expandedContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {includes.length > 0 && (
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.5rem 0' }}>
            O que está incluso
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {includes.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0',
                borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
              }}>
                <CheckCircle size={14} color="#5DD4C3" strokeWidth={2} />
                <span style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Consulados (quando com entrevista) */}
      {interviewRequired && consulateCities.length > 0 && (
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.5rem 0' }}>
            Consulados disponíveis
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {consulateCities.map((city, idx) => (
              <div key={idx} style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.375rem 0.75rem',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '0.5rem'
              }}>
                <MapPin size={12} color="rgba(255, 255, 255, 0.6)" />
                <span style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                  {city}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <StorytellingLayout
      backgroundImage={backgroundImage}
      title="Visto Americano"
      narrativeText={narrativeText || getDefaultNarrative()}
      stepIndicator={stepIndicator}
      ctaText={ctaText}
      onContinue={onContinue}
      onBack={onBack}
      onShare={onShare}
    >
      <ExpandableCard expandedContent={expandedContent}>
        {cardContent}
      </ExpandableCard>
    </StorytellingLayout>
  );
}
